/**
 * Day and week arithmetic for the robot lanes, on the SITE's calendar.
 *
 * The old week grid drew on the browser's clock and said so in a banner.
 * The lanes do not: a day here is midnight-to-midnight where the robots
 * are, because "Thursday" for a Singapore hotel is a Singapore Thursday
 * whoever is reading it. Days are named by `DateKey` (`2026-10-30`), a
 * calendar date with no zone in it, and turned into instants only through
 * `zoned-time.ts`. A DST day is therefore 23 or 25 hours long and every
 * hour tick lands where that zone's clock reads HH:00.
 */
import { formatZonedClock, wallClockToEpochMs, zonedParts } from '../zoned-time';

/** `YYYY-MM-DD`, a date on the site's calendar. */
export type DateKey = string;

export interface TimeWindow {
  /** Inclusive. */
  fromMs: number;
  /** Exclusive. */
  toMs: number;
}

/** The working-day strip the Day view opens on and the Week cells draw.
 *  The rest of the day is a scroll (Day) or the "24 h" toggle away. */
export const WORK_START_HOUR = 7;
export const WORK_END_HOUR = 19;

const DATE_KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

export function isDateKey(value: unknown): value is DateKey {
  if (typeof value !== 'string') return false;
  const m = DATE_KEY_RE.exec(value);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const probe = new Date(Date.UTC(y, mo - 1, d));
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === mo - 1 && probe.getUTCDate() === d;
}

function parts(key: DateKey): { year: number; month: number; day: number } {
  const m = DATE_KEY_RE.exec(key);
  if (!m) throw new Error(`not a date key: ${key}`);
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

/** The site-calendar date `epochMs` falls on. */
export function dateKeyOf(epochMs: number, timeZone: string): DateKey {
  const p = zonedParts(epochMs, timeZone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

/** Calendar arithmetic on the date alone -- no instant, no zone. */
export function addDays(key: DateKey, n: number): DateKey {
  const { year, month, day } = parts(key);
  const d = new Date(Date.UTC(year, month - 1, day + n));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** 0 = Sunday, like `Date.getDay`. */
export function weekdayOf(key: DateKey): number {
  const { year, month, day } = parts(key);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

/** The Monday on or before `key`. The lanes' week runs Monday to Sunday
 *  (the approved mockup), and so does the Month grid's (2026-10-31). */
export function weekStart(key: DateKey): DateKey {
  const dow = weekdayOf(key);
  return addDays(key, dow === 0 ? -6 : 1 - dow);
}

/** The instant the site's clock reads `hour`:00 on `key`. `hour` may be
 *  24 (the next midnight). */
export function instantAt(key: DateKey, hour: number, timeZone: string): number {
  const base = hour >= 24 ? addDays(key, Math.floor(hour / 24)) : key;
  const { year, month, day } = parts(base);
  return wallClockToEpochMs({ year, month, day, hour: hour % 24, minute: 0, second: 0 }, timeZone);
}

/** Midnight to midnight on the site's calendar. */
export function dayWindow(key: DateKey, timeZone: string): TimeWindow {
  return { fromMs: instantAt(key, 0, timeZone), toMs: instantAt(addDays(key, 1), 0, timeZone) };
}

export function weekDays(key: DateKey): DateKey[] {
  const start = weekStart(key);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

export function weekWindow(key: DateKey, timeZone: string): TimeWindow {
  const start = weekStart(key);
  return { fromMs: instantAt(start, 0, timeZone), toMs: instantAt(addDays(start, 7), 0, timeZone) };
}

/** Each hour boundary of the day, as the site's clock shows it. */
export function hourTicks(key: DateKey, timeZone: string): Array<{ ms: number; label: string }> {
  const out: Array<{ ms: number; label: string }> = [];
  const { toMs } = dayWindow(key, timeZone);
  for (let h = 0; h <= 24; h += 1) {
    const ms = h === 24 ? toMs : instantAt(key, h, timeZone);
    out.push({ ms, label: h === 24 ? '24:00' : formatZonedClock(ms, timeZone) });
  }
  return out;
}

/** True when the two windows share any instant. */
export function overlaps(a: TimeWindow, b: TimeWindow): boolean {
  return a.fromMs < b.toMs && b.fromMs < a.toMs;
}

export const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
export const WEEKDAY_LONG = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;
export const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** `30/Oct/2026` -- the house date form (`utils/date-format.ts`), built
 *  from the key so no zone can shift it. */
export function formatKey(key: DateKey): string {
  const { year, month, day } = parts(key);
  return `${pad(day)}/${MONTH_SHORT[month - 1]}/${year}`;
}

/** `Thu 22`. */
export function shortDayLabel(key: DateKey): string {
  return `${WEEKDAY_SHORT[weekdayOf(key)]} ${parts(key).day}`;
}

/** `19/Oct – 25/Oct/2026`. */
export function weekLabel(key: DateKey): string {
  const days = weekDays(key);
  const a = parts(days[0]!);
  const b = parts(days[6]!);
  const left = `${pad(a.day)}/${MONTH_SHORT[a.month - 1]}${a.year === b.year ? '' : `/${a.year}`}`;
  return `${left} – ${formatKey(days[6]!)}`;
}

/** `09:05–09:17`, on the site's clock. */
export function formatRange(fromMs: number, toMs: number, timeZone: string): string {
  return `${formatZonedClock(fromMs, timeZone)}–${formatZonedClock(toMs, timeZone)}`;
}

/** `12 min`, `1 h 05 min`, `under 1 min`. */
export function formatDuration(ms: number): string {
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'under 1 min';
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} h ${pad(min % 60)} min`;
}

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** A Block-time form's day and two `HH:MM`s as a window on the site's
 *  clock; a To at or before the From runs past midnight. */
export function blockRange(
  day: DateKey,
  from: string,
  to: string,
  timeZone: string,
): { startMs: number; endMs: number } | null {
  const a = TIME_RE.exec(from);
  const b = TIME_RE.exec(to);
  if (!a || !b) return null;
  const at = (key: DateKey, hh: number, mm: number) => {
    const [y, mo, d] = key.split('-').map(Number) as [number, number, number];
    return wallClockToEpochMs(
      { year: y, month: mo, day: d, hour: hh, minute: mm, second: 0 },
      timeZone,
    );
  };
  const startMs = at(day, Number(a[1]), Number(a[2]));
  let endMs = at(day, Number(b[1]), Number(b[2]));
  if (endMs <= startMs) endMs = at(addDays(day, 1), Number(b[1]), Number(b[2]));
  return { startMs, endMs };
}
